// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useLayoutEffect, useRef, useState } from 'react';

import { blendFor, parseCssColor, type Rgba } from '../logo/color';
import {
  DEFAULT_GEOMETRY,
  geometryFromMetrics,
  WORD_TEXT,
  wordLayout,
  type WordLayout,
  type XGeometry,
} from '../logo/geometry';
import { BRAND_WEIGHT, drawFrame, type LogoPalette } from '../logo/paint';
import { PARTS } from '../logo/parts';
import { DEFAULT_SEED, generateModel, type LogoModel } from '../logo/points';
import { isDone, morph, nextPart, REST_S } from '../logo/timeline';
import styles from './Logo.module.css';

export interface LogoProps {
  /** `word`: "ReUse" + the X; `mark`: the X alone. */
  variant?: 'word' | 'mark';
  /** The X's cap height in CSS px (the bars use 14, Login 28). */
  size?: number;
  /** Play the first morph shortly after mounting instead of after a full rest. */
  playOnMount?: boolean;
  className?: string;
}

/** The family `document.fonts` is asked to load before the X is measured. */
const FONT_PROBE = `${BRAND_WEIGHT} 100px "Barlow Semi Condensed"`;
/** How long to wait for the brand face before measuring whatever renders. */
const FONT_WAIT_MS = 2500;
/** The first play with `playOnMount`. */
const FIRST_PLAY_MS = 600;
const MAX_DPR = 2.5;
/** Used only when `--font-brand` cannot be read. */
const BRAND_FONT_FALLBACK = "'Barlow Semi Condensed', 'Arial Narrow', sans-serif";
/** Used only when the element's `color` cannot be parsed. */
const INK_FALLBACK: Rgba = { r: 0, g: 0, b: 0, a: 1 };

/** The X measured off the loaded brand face, shared by every Logo once known. */
let cachedGeometry: XGeometry | null = null;

/** Port of the reference's measure(): the X's cap, aspect and stroke width at 200 px. */
function measureGeometry(fontFamily: string): XGeometry | null {
  const CW = 560;
  const c = document.createElement('canvas');
  c.width = CW;
  c.height = 320;
  const x = c.getContext('2d', { willReadFrequently: true });
  if (!x) return null;
  const F = 200;
  x.font = `${BRAND_WEIGHT} ${F}px ${fontFamily}`;
  const m = x.measureText('X');
  const ascent = m.actualBoundingBoxAscent;
  const width = m.actualBoundingBoxLeft + m.actualBoundingBoxRight;
  if (!(ascent > 0 && width > 0)) return null;
  // A mask, never shown: the default fill is opaque, which is all the scan reads.
  const ox = 40 + m.actualBoundingBoxLeft;
  const oy = 280;
  x.fillText('X', ox, oy);
  // One row just under the top of the X: its first run of ink is one stroke.
  const row = Math.round(oy - ascent + ascent * 0.03);
  const px = x.getImageData(0, row, CW, 1).data;
  let s = -1;
  let e = -1;
  for (let i = 0; i < CW; i++) {
    const on = px[i * 4 + 3] > 128;
    if (on && s < 0) s = i;
    if (!on && s >= 0) {
      e = i;
      break;
    }
  }
  return geometryFromMetrics({ F, ascent, width, stemWidth: s >= 0 && e > s ? e - s : null });
}

/** The theme's colours for the logo, read off the element (so `color` is whatever it inherits). */
function readPalette(el: HTMLElement): LogoPalette {
  const cs = getComputedStyle(el);
  const prop = (name: string) => cs.getPropertyValue(name).trim();
  const ink = parseCssColor(cs.color) ?? INK_FALLBACK;
  const accent = parseCssColor(prop('--color-accent')) ?? ink;
  return {
    ink,
    accent,
    partColors: PARTS.map((p) => parseCssColor(prop(p.colorToken)) ?? accent),
    font: prop('--font-brand') || BRAND_FONT_FALLBACK,
  };
}

/**
 * The ReUseX wordmark: "ReUse" and an X in the brand face, one stroke of the
 * X drawn as a scanned point cloud. Every `REST_S` seconds (or on hover) the
 * X turns into a building part and back; otherwise it is static. Respects
 * prefers-reduced-motion (always static) and stops while the tab is hidden.
 * Without a 2D canvas it falls back to plain text in the brand face.
 */
export function Logo({ variant = 'word', size = 14, playOnMount = false, className }: LogoProps) {
  const rootRef = useRef<HTMLSpanElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [fallback, setFallback] = useState(false);

  // A layout effect: the canvas is sized and drawn before the first paint, so nothing jumps.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;
    root.style.setProperty('--logo-cap', `${size}px`);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setFallback(true);
      return;
    }

    const hasText = variant === 'word';
    let alive = true;
    let ready = false;
    let g = cachedGeometry ?? DEFAULT_GEOMETRY;
    let palette = readPalette(root);
    let blend = blendFor(palette.ink);
    // Both set by build(), which runs before anything draws.
    let layout!: WordLayout;
    let model!: LogoModel;
    let scale = 1;
    let part = 0;
    let playing = false;
    let playStart = 0;
    let lastM = 0;
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let fontTimer: ReturnType<typeof setTimeout> | undefined;

    const build = () => {
      const F = size / g.capEm;
      ctx.font = `${BRAND_WEIGHT} ${F}px ${palette.font}`;
      const textWidth = hasText ? ctx.measureText(WORD_TEXT).width : 0;
      const xLeftBearing = -ctx.measureText('X').actualBoundingBoxLeft;
      layout = wordLayout({ F, g, textWidth, xLeftBearing, hasText });
      model = generateModel(layout, g, DEFAULT_SEED);
      scale = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      canvas.width = Math.round(layout.W * scale);
      canvas.height = Math.round(layout.H * scale);
      canvas.style.width = `${layout.W}px`;
      canvas.style.height = `${layout.H}px`;
      // The padding is room for the points' arc; the element itself is the ink's size.
      canvas.style.margin = `${-layout.pad}px ${-layout.side}px`;
    };

    const draw = (m: number) => {
      lastM = m;
      drawFrame(ctx, model, layout, g, { m, part }, palette, blend, scale);
    };

    const reducedQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
    let reduced = reducedQuery?.matches ?? false;

    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      clearTimeout(timer);
      timer = undefined;
    };

    /** Back to the static X, and the next play after `ms` (unless motion is off or the tab hidden). */
    const rest = (ms: number) => {
      stop();
      if (playing) {
        playing = false;
        draw(0);
      }
      if (!alive || !ready || reduced || document.hidden) return;
      timer = setTimeout(play, ms);
    };

    const tick = (now: number) => {
      const elapsed = (now - playStart) / 1000;
      if (isDone(elapsed)) {
        playing = false;
        part = nextPart(part, PARTS.length);
        draw(0);
        rest(REST_S * 1000);
        return;
      }
      draw(morph(elapsed));
      raf = requestAnimationFrame(tick);
    };

    function play() {
      if (!alive || playing || !ready || reduced || document.hidden) return;
      stop();
      playing = true;
      playStart = performance.now();
      raf = requestAnimationFrame(tick);
    }

    const firstRest = () => {
      ready = true;
      rest(playOnMount ? FIRST_PLAY_MS : REST_S * 1000);
    };

    /** The brand face is in (or gave up on): measure the X off it and redraw. */
    const settle = () => {
      if (!alive) return;
      const measured = measureGeometry(palette.font);
      if (measured) {
        g = measured;
        if (document.fonts?.check(FONT_PROBE)) cachedGeometry = measured;
      }
      build();
      draw(0);
      firstRest();
    };

    // Drawn at once with what renders now; redrawn when the face is in.
    build();
    draw(0);
    if (cachedGeometry) firstRest();
    else if (!document.fonts || document.fonts.check(FONT_PROBE)) settle();
    else {
      const timeout = new Promise<void>((resolve) => {
        fontTimer = setTimeout(resolve, FONT_WAIT_MS);
      });
      Promise.race([document.fonts.load(FONT_PROBE, 'ReUseX'), timeout])
        .catch(() => undefined)
        .then(settle);
    }

    const onEnter = () => play();
    const onVisibility = () => rest(REST_S * 1000);
    const onReduced = () => {
      reduced = reducedQuery?.matches ?? false;
      rest(REST_S * 1000);
    };
    // The theme swaps every token under `data-theme` on <html> (as in the Ground).
    const observer =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(() => {
            palette = readPalette(root);
            blend = blendFor(palette.ink);
            if (!playing) draw(lastM);
          });
    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    root.addEventListener('pointerenter', onEnter);
    document.addEventListener('visibilitychange', onVisibility);
    reducedQuery?.addEventListener('change', onReduced);

    return () => {
      alive = false;
      stop();
      clearTimeout(fontTimer);
      observer?.disconnect();
      root.removeEventListener('pointerenter', onEnter);
      document.removeEventListener('visibilitychange', onVisibility);
      reducedQuery?.removeEventListener('change', onReduced);
    };
  }, [variant, size, playOnMount]);

  return (
    <span ref={rootRef} className={className ? `${styles.logo} ${className}` : styles.logo} role="img" aria-label="ReUseX">
      <canvas ref={canvasRef} className={styles.canvas} width={0} height={0} hidden={fallback} aria-hidden="true" />
      {fallback && (
        <span className={styles.fallback} aria-hidden="true">
          {variant === 'word' ? `${WORD_TEXT}X` : 'X'}
        </span>
      )}
    </span>
  );
}
